// src/components/module/reportes/Filters/ReportFilters.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Chip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  FilterList,
  Search,
  Close,
  ExpandMore,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FiltroReporte } from '../../../types/reportes.types';
import { ReportesApi } from '../../../../services/api/reportesApi';
import axios from 'axios';
import './reportFilters.scss';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4020';

interface ReportFiltersProps {
  onFiltrosChange: (filtros: FiltroReporte) => void;
}

interface Sucursal {
  Id: number;
  Nombre: string;
}

export const ReportFilters: React.FC<ReportFiltersProps> = ({ onFiltrosChange }) => {
  // Estados de filtros
  const [periodo, setPeriodo] = useState<string>('mes');
  const [fechaInicio, setFechaInicio] = useState<Date | null>(null);
  const [fechaFin, setFechaFin] = useState<Date | null>(null);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState<string>('');
  const [incluirHistorico, setIncluirHistorico] = useState(true);
  const [limite, setLimite] = useState<number>(10);
  const [expanded, setExpanded] = useState(false);

  // Estados de datos
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(true);

  // Filtros activos
  const [filtrosActivos, setFiltrosActivos] = useState<string[]>([]);

  // ✅ FIX: Ref para controlar si el componente está montado
  const isMountedRef = useRef(true);

  // ✅ FIX: Cleanup al desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Cargar sucursales
  useEffect(() => {
    cargarSucursales();
  }, []);

  // ✅ FIX: Eliminar el useEffect automático que causaba el bucle
  // Ahora los filtros solo se aplican cuando el usuario hace clic en "Aplicar"

  const cargarSucursales = async () => {
    try {
      setLoadingSucursales(true);
      const { data } = await axios.post(`${API_URL}/administracion/sucursales/findAll`);
      
      // ✅ FIX: Solo actualizar si el componente está montado
      if (isMountedRef.current) {
        setSucursales(data || []);
      }
    } catch (error) {
      console.error('Error al cargar sucursales:', error);
    } finally {
      // ✅ FIX: Solo actualizar si el componente está montado
      if (isMountedRef.current) {
        setLoadingSucursales(false);
      }
    }
  };

  const aplicarFiltros = () => {
    const filtros: FiltroReporte = {
      incluirHistorico,
      limite,
    };

    const activos: string[] = [];

    // Fechas según período
    if (periodo === 'personalizado') {
      if (fechaInicio) {
        filtros.fechaInicio = fechaInicio.toISOString().split('T')[0];
        activos.push(`Desde: ${filtros.fechaInicio}`);
      }
      if (fechaFin) {
        filtros.fechaFin = fechaFin.toISOString().split('T')[0];
        activos.push(`Hasta: ${filtros.fechaFin}`);
      }
    } else {
      const fechas = ReportesApi.obtenerFechasPorPeriodo(periodo);
      filtros.fechaInicio = fechas.fechaInicio;
      filtros.fechaFin = fechas.fechaFin;
      activos.push(`Período: ${getPeriodoLabel(periodo)}`);
    }

    // Sucursal
    if (sucursalSeleccionada) {
      filtros.idSucursal = Number(sucursalSeleccionada);
      const sucursal = sucursales.find(s => s.Id.toString() === sucursalSeleccionada);
      if (sucursal) {
        activos.push(`Sucursal: ${sucursal.Nombre}`);
      }
    }

    // Histórico
    if (!incluirHistorico) {
      activos.push('Sin datos históricos');
    }

    setFiltrosActivos(activos);
    onFiltrosChange(filtros);
  };

  const limpiarFiltros = () => {
    setPeriodo('mes');
    setFechaInicio(null);
    setFechaFin(null);
    setSucursalSeleccionada('');
    setIncluirHistorico(true);
    setLimite(10);
    setFiltrosActivos([]);

    // Aplicar filtros por defecto
    const fechas = ReportesApi.obtenerFechasPorPeriodo('mes');
    onFiltrosChange({
      fechaInicio: fechas.fechaInicio,
      fechaFin: fechas.fechaFin,
      incluirHistorico: true,
      limite: 10,
    });
  };

  const eliminarFiltro = (filtro: string) => {
    if (filtro.startsWith('Período:')) {
      setPeriodo('mes');
    } else if (filtro.startsWith('Sucursal:')) {
      setSucursalSeleccionada('');
    } else if (filtro.startsWith('Desde:')) {
      setFechaInicio(null);
    } else if (filtro.startsWith('Hasta:')) {
      setFechaFin(null);
    } else if (filtro === 'Sin datos históricos') {
      setIncluirHistorico(true);
    }
    
    // ✅ FIX: Aplicar filtros solo una vez después de actualizar el estado
    setTimeout(() => aplicarFiltros(), 100);
  };

  const getPeriodoLabel = (valor: string): string => {
    const labels: { [key: string]: string } = {
      hoy: 'Hoy',
      ayer: 'Ayer',
      semana: 'Última semana',
      mes: 'Este mes',
      trimestre: 'Este trimestre',
      ano: 'Este año',
      personalizado: 'Personalizado',
    };
    return labels[valor] || valor;
  };

  return (
    <Box className="report-filters">
      {/* Filtros principales */}
      <Grid container spacing={2} alignItems="center">
        {/* Período predefinido */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Período</InputLabel>
            <Select
              value={periodo}
              label="Período"
              onChange={(e) => setPeriodo(e.target.value)}
            >
              <MenuItem value="hoy">Hoy</MenuItem>
              <MenuItem value="ayer">Ayer</MenuItem>
              <MenuItem value="semana">Última semana</MenuItem>
              <MenuItem value="mes">Este mes</MenuItem>
              <MenuItem value="trimestre">Este trimestre</MenuItem>
              <MenuItem value="ano">Este año</MenuItem>
              <MenuItem value="personalizado">Personalizado</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Sucursal */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small" disabled={loadingSucursales}>
            <InputLabel>Sucursal</InputLabel>
            <Select
              value={sucursalSeleccionada}
              label="Sucursal"
              onChange={(e) => setSucursalSeleccionada(e.target.value)}
            >
              <MenuItem value="">
                <em>Todas las sucursales</em>
              </MenuItem>
              {sucursales.map((sucursal) => (
                <MenuItem key={sucursal.Id} value={sucursal.Id.toString()}>
                  {sucursal.Nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Límite de resultados */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Límite</InputLabel>
            <Select
              value={limite}
              label="Límite"
              onChange={(e) => setLimite(Number(e.target.value))}
            >
              <MenuItem value={5}>5 registros</MenuItem>
              <MenuItem value={10}>10 registros</MenuItem>
              <MenuItem value={20}>20 registros</MenuItem>
              <MenuItem value={50}>50 registros</MenuItem>
              <MenuItem value={100}>100 registros</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Botones de acción */}
        <Grid item xs={12} sm={6} md={4}>
          <Box display="flex" gap={1} justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={aplicarFiltros}
              size="medium"
            >
              Aplicar
            </Button>
            <Button
              variant="outlined"
              startIcon={<Close />}
              onClick={limpiarFiltros}
              size="medium"
            >
              Limpiar
            </Button>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setExpanded(!expanded)}
              size="medium"
            >
              {expanded ? 'Menos' : 'Más'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Filtros avanzados (desplegable) */}
      <Accordion 
        expanded={expanded} 
        onChange={() => setExpanded(!expanded)}
        elevation={0}
        sx={{ mt: 2, '&:before': { display: 'none' } }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMore />}
          sx={{ display: 'none' }}
        >
          <Typography>Filtros avanzados</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Fechas personalizadas */}
            {periodo === 'personalizado' && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Fecha inicio"
                    value={fechaInicio}
                    onChange={(newValue) => setFechaInicio(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Fecha fin"
                    value={fechaFin}
                    onChange={(newValue) => setFechaFin(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
              </>
            )}

            {/* Incluir histórico */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={incluirHistorico}
                    onChange={(e) => setIncluirHistorico(e.target.checked)}
                    color="primary"
                  />
                }
                label="Incluir datos históricos"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Filtros activos */}
      {filtrosActivos.length > 0 && (
        <Box className="filtros-activos" sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Filtros activos:
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
            {filtrosActivos.map((filtro, index) => (
              <Chip
                key={index}
                label={filtro}
                size="small"
                onDelete={() => eliminarFiltro(filtro)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};